import { FC, useState, useEffect, useRef } from "react";
import Proptypes from "prop-types";
import {
  Typography,
  Grid,
  Button,
  Popover,
  Divider,
} from "@material-ui/core";
import { InputField } from './input-field';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from '../store';
import { getLists } from '../slices/listSlice'
import { PndSelector } from "./review-1/pnd/pnd-selector";

interface PopoverListProps {
  type: string;
  element: any;
  disabled: boolean;
  onBlurInput: (element: any) => void;
  /** Open the popover automatically (e.g. a row just added via "+"). */
  autoOpen?: boolean;
  /** Called once the popover has auto-opened, so the caller can clear its trigger. */
  onAutoOpened?: () => void;
}

export const PopoverList: FC<PopoverListProps> = (props) => {
  const { type, element, disabled, onBlurInput, autoOpen, onAutoOpened } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [activeElement, setActiveElement] = useState({
    description: '',
    move: '',
    type: 1 as 1 | 2 | 3,
    id: null
  });

  const [activeAspirations, setActiveAspirations] = useState([]);
  const [activeResistances, setActiveResistances] = useState([]);

  const { aspirations, resistances } = useSelector((state: RootState) => state.list);
  const dispatch = useDispatch();

  useEffect(() => {
    if (element) {
      setActiveElement(element);
    }
  }, [element]);

  useEffect(() => {
    if (activeElement.description !== '') {
      var newAspirations = aspirations.filter(el => el.description.toLowerCase().includes(activeElement.description.toLowerCase()));
      var newResistances = resistances.filter(el => el.description.toLowerCase().includes(activeElement.description.toLowerCase()));
      setActiveAspirations(newAspirations);
      setActiveResistances(newResistances);
    }

  }, [activeElement]);

  useEffect(() => {
    setActiveAspirations(aspirations);
    setActiveResistances(resistances);
  }, [aspirations, resistances]);

  useEffect(() => {
    dispatch(getLists());

  }, []);

  // Auto-open the popover when the row was just added via the "+" button, so the
  // user lands directly in the input without an extra click.
  useEffect(() => {
    if (autoOpen && buttonRef.current) {
      setAnchorEl(buttonRef.current);
      onAutoOpened && onAutoOpened();
    }
  }, [autoOpen]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Commit edits up to the parent (and therefore Redux) as they are typed, not only on
  // close. Switching wizard step/section unmounts this popover without firing its onClose,
  // which previously discarded any unconfirmed input (data-loss bug
  // 20260717-bug-stakeholder-aspirations-resistances-not-saved). This reuses the same
  // index-bound onBlurInput already used by the "select an existing element" flow.
  const commitChange = (next: typeof activeElement) => {
    setActiveElement(next);
    onBlurInput(next);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onBlurInput(activeElement);
  };

  const listLabel = type === 'aspiration' ? 'aspirations' : 'resistances';
  const currentItems = type === 'aspiration' ? activeAspirations : activeResistances;
  const sourceEmpty = (type === 'aspiration' ? aspirations : resistances).length === 0;

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <>
      <Button ref={buttonRef} fullWidth onClick={handleClick} sx={{ padding: '0px 0px 0px 5px !important' }} disabled={props.disabled}>
        <span
          aria-describedby={id}
          style={{ flexGrow: 1, textAlign: "left", paddingTop: type === 'resistance' && '5px', paddingBottom: type === 'resistance' && '5px' }}
        >
          {activeElement.description === '' ? <i>Click to add element</i> : activeElement.description}
        </span>
        {type === 'aspiration' && (
          <PndSelector currentPnd={activeElement.type} disabled />
        )}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{ '& .MuiPaper-root': { backgroundColor: 'background.default' } }}
      >
        <Grid p={1}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, display: 'block', mb: 0.5 }}>
            {`Type a new ${type === 'aspiration' ? 'aspiration' : 'resistance'}, or pick an existing one below`}
          </Typography>
          <Grid container width={500} spacing={1}>
            <Grid item xs={type === 'aspiration' ? 9 : 12}>
              <InputField
                fullWidth
                autoFocus
                placeholder="Enter new element description"
                size="small"
                value={activeElement.description}
                onChange={(e) => commitChange({ ...activeElement, description: e.target.value, id: null })}
                disabled={props.disabled}
              />
            </Grid>
            {/* The free-text "action to reduce resistance" input was removed: tactical
                actions on a resistance are now real Move records attached through
                <ResistanceMovesPanel>, and two competing "action" concepts on the same
                row is exactly what the feature was asked to avoid. The stored `move`
                string is left untouched on existing rows — a handful of projects have
                real text in it that should be migrated into Moves separately. */}
            {type === 'aspiration' && (
              <Grid item xs={3}>
                <PndSelector
                  currentPnd={activeElement.type}
                  disabled={props.disabled}
                  size="small"
                  setPnd={(pnd) => commitChange({ ...activeElement, type: pnd as 1 | 2 | 3 })}
                />
              </Grid>
            )}
          </Grid>
          <Grid p={0.5}>
            <Divider />
          </Grid>
          <Grid container width={500} justifyContent="space-between" alignItems="center" sx={{ px: 0.5, pb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {`Existing ${listLabel}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {`${currentItems?.length || 0} ${(currentItems?.length || 0) === 1 ? 'match' : 'matches'}`}
            </Typography>
          </Grid>
          {(!currentItems || currentItems.length === 0) ? (
            <Grid container width={500} sx={{ px: 0.5, py: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {sourceEmpty
                  ? `No ${listLabel} created yet — type above to add one.`
                  : `No matching ${listLabel}.`}
              </Typography>
            </Grid>
          ) : type === 'aspiration' ? (
            currentItems.map(item => (
              <Grid container width={500} spacing={0.5} sx={{ borderRadius: 1, backgroundColor: item.id != null && item.id === activeElement.id ? 'action.selected' : 'transparent' }}>
                <Grid item xs={9} sx={{ cursor: 'pointer' }} onClick={() => {
                  onBlurInput({ description: item.description, id: item.id, type: item.type });
                  setAnchorEl(null);
                }}>
                  <Typography>{item.description}</Typography>
                </Grid>
                <Grid item textAlign={'right'} xs={3}>
                  <PndSelector currentPnd={item.type} disabled size="small" />
                </Grid>
                <Grid xs={12} item p={0.5}>
                  <Divider />
                </Grid>
              </Grid>
            ))
          ) : (
            currentItems.map(item => (
              <Grid container width={500} spacing={1} sx={{ borderRadius: 1, backgroundColor: item.id != null && item.id === activeElement.id ? 'action.selected' : 'transparent' }}>
                <Grid item xs={12} sx={{ cursor: 'pointer', mb: 2 }} onClick={() => {
                  onBlurInput({ description: item.description, id: item.id, type: item.type });
                  setAnchorEl(null);
                }}>
                  <Typography>{item.description}</Typography>
                </Grid>
              </Grid>
            ))
          )}
          <Grid p={1}>
            <Divider />
          </Grid>
          <Grid p={1} container width={500} spacing={1}>
            <Grid item xs={12}>
              <Button
                size="small"
                variant="contained"
                fullWidth
                onClick={() => handleClose()}
              >{`Update ${type === 'aspiration' ? 'aspiration' : 'resistance'}`}</Button>
            </Grid>
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

PopoverList.propTypes = {
  type: Proptypes.string,
  element: Proptypes.object.isRequired,
  disabled: Proptypes.bool,
  onBlurInput: Proptypes.func
};